import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Loader2, ArrowRight } from "lucide-react";

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(faqs.map((f) => f.category)))];
  const filtered = activeCategory === "All" ? faqs : faqs.filter((f) => f.category === activeCategory);

  return (
    <div data-testid="faq-page">
      <section className="border-b border-zinc-800">
        <div className="container-pl py-16 lg:py-20">
          <p className="overline mb-4">Common questions</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-zinc-50 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-zinc-300 max-w-2xl leading-relaxed">
            Clear answers about our services, scope, scheduling, and confidentiality.
          </p>
        </div>
      </section>

      <section className="container-pl py-16">
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400 mx-auto" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <p className="overline mb-4">Categories</p>
              <ul className="space-y-1" data-testid="faq-categories">
                {categories.map((c) => (
                  <li key={c}>
                    <button
                      onClick={() => setActiveCategory(c)}
                      className={`text-sm py-1.5 px-3 rounded-sm w-full text-left transition-colors ${
                        activeCategory === c
                          ? "bg-blue-700 text-white"
                          : "text-zinc-300 hover:bg-zinc-900"
                      }`}
                      data-testid={`faq-category-${c.toLowerCase()}`}
                    >
                      {c}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <div className="lg:col-span-9">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {filtered.map((f, i) => (
                  <AccordionItem key={`${f.category}-${f.question}`} value={`item-${i}`} className="border-b border-zinc-800" data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left text-base font-medium text-zinc-50 hover:text-amber-400 py-6">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-zinc-300 leading-relaxed pb-6">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="mt-16 p-8 border border-zinc-800 rounded-sm bg-black">
                <h3 className="text-xl font-medium text-zinc-50 mb-2">Still have questions?</h3>
                <p className="text-zinc-300 mb-6">Schedule a consultation. We're here to listen.</p>
                <Link to="/booking">
                  <Button className="rounded-sm bg-blue-700 hover:bg-blue-600" data-testid="faq-schedule-btn">
                    Schedule a Consultation
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
